// src/components/QRScanner.js - Bổ sung điểm danh thủ công toàn trường
import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { firestore } from '../firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { format } from 'date-fns';
import { StudentContext } from '../StudentContext';
import qrScanIcon from './access/qr-code-scan.svg';
import './QRScanner.css';

const QRScanner = () => {
  const { currentYearId, studentsData, classMap, updateAttendanceInContext } = useContext(StudentContext);
  const [hasAccess, setHasAccess] = useState(null);
  const [scanResults, setScanResults] = useState(() => {
    const savedResults = sessionStorage.getItem('scanResults');
    return savedResults ? JSON.parse(savedResults).map(result => ({
      ...result,
      timestamp: new Date(result.timestamp)
    })) : [];
  });
  const [scanning, setScanning] = useState(false);

  // State cho chức năng điểm danh thủ công
  const [manualInput, setManualInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const catechistFullName = localStorage.getItem('catechistFullName') || 'Giáo lý viên';
  const catechistHolyName = localStorage.getItem('catechistHolyName') || '';
  const scannerRef = useRef(null);

  // Gộp danh sách học sinh toàn trường từ studentsData
  const allStudents = useMemo(() => {
    if (!studentsData) return [];
    const flatList = Object.values(studentsData).flat();
    const seen = new Set();
    return flatList.filter(student => {
      if (!student.id || seen.has(student.id)) return false;
      seen.add(student.id);
      return true;
    });
  }, [studentsData]);

  // Lọc gợi ý tìm kiếm theo mã ID hoặc Họ và tên
  useEffect(() => {
    if (manualInput.trim() === '') {
      setSuggestions([]);
      return;
    }
    const queryText = manualInput.toLowerCase().trim();
    const filtered = allStudents.filter(s =>
      (s.id && s.id.toLowerCase().includes(queryText)) ||
      (s.fullName && s.fullName.toLowerCase().includes(queryText))
    );
    // Giới hạn 15 kết quả để giao diện mượt mà
    setSuggestions(filtered.slice(0, 15));
  }, [manualInput, allStudents]);

  useEffect(() => {
    sessionStorage.setItem('scanResults', JSON.stringify(scanResults));
  }, [scanResults]);

  useEffect(() => {
    const checkAccess = async () => {
      const uid = localStorage.getItem('uid');
      const role = localStorage.getItem('userRole');
      const departmentLeader = localStorage.getItem('departmentLeader');

      if (role === 'admin' || departmentLeader === 'Ban Kỷ Luật' || departmentLeader === 'Ban Hành Chánh') {
        setHasAccess(true);
        return;
      }

      if (uid) {
        try {
          const staffSnap = await getDoc(doc(firestore, 'staff', uid));
          if (staffSnap.exists()) {
            setHasAccess(staffSnap.data().churchAttendanceAccess ?? true);
          } else {
            setHasAccess(false);
          }
        } catch (err) {
          setHasAccess(true);
        }
      } else {
        setHasAccess(false);
      }
    };

    checkAccess();
  }, []);

  useEffect(() => {
    if (scanning && !scannerRef.current) {
      setTimeout(() => {
        try {
          scannerRef.current = new Html5QrcodeScanner(
            'reader',
            {
              fps: 10,
              qrbox: { width: 220, height: 220 },
              facingMode: 'environment',
              showTorchButton: true,
              showZoomSlider: true,
            },
            false
          );
          scannerRef.current.render(onScanSuccess, () => { });
        } catch (error) {
          console.error('Lỗi scanner:', error);
          setScanning(false);
        }
      }, 100);
    } else if (!scanning && scannerRef.current) {
      scannerRef.current.clear().catch(console.error);
      scannerRef.current = null;
    }

    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear().catch(console.error);
        scannerRef.current = null;
      }
    };
  }, [scanning]);

  const addScanResult = (result) => {
    setScanResults(prev => {
      if (result.status !== 'processing') {
        const newResults = prev.map(item =>
          item.studentId === result.studentId && item.status === 'processing' ? result : item
        );
        if (!newResults.some(item => item.studentId === result.studentId)) {
          return [result, ...newResults].slice(0, 15);
        }
        return newResults.slice(0, 15);
      } else {
        return [result, ...prev].slice(0, 15);
      }
    });
  };

  const processAttendance = async (studentId) => {
    const now = new Date();
    const dateKey = format(now, 'yyyy-MM-dd');
    const timeString = format(now, 'HH:mm');

    addScanResult({ studentId, name: 'Đang xử lý...', timestamp: now, status: 'processing' });

    try {
      const studentSnap = await getDoc(doc(firestore, 'students', studentId));
      if (!studentSnap.exists()) {
        addScanResult({ studentId, name: 'Mã ID không tồn tại', timestamp: now, status: 'error' });
        return;
      }

      const studentData = studentSnap.data();
      const studentName = `${studentData.holyName || ''} ${studentData.fullName || ''}`.trim();

      const docRef = doc(firestore, `academic_years/${currentYearId}/student_attendances`, studentId);

      await setDoc(docRef, {
        church: {
          [dateKey]: {
            time: `Có lúc ${timeString}`,
            catechist: catechistFullName,
            time_log: now.toISOString()
          }
        }
      }, { merge: true });

      if (typeof updateAttendanceInContext === 'function') {
        updateAttendanceInContext(studentId, 'church', dateKey, `Có lúc ${timeString}`, catechistFullName);
      }

      addScanResult({ studentId, name: studentName, timestamp: now, status: 'success' });

    } catch (error) {
      console.error('Lỗi khi điểm danh đi lễ:', error);
      addScanResult({ studentId, name: 'Lỗi ghi dữ liệu', timestamp: now, status: 'error' });
    }
  };

  const onScanSuccess = async (decodedText) => {
    if (scannerRef.current) {
      scannerRef.current.clear().catch(console.error);
      scannerRef.current = null;
    }
    setScanning(false);
    await processAttendance(decodedText.trim());
  };

  const handleSuggestionClick = (student) => {
    setSelectedStudent(student);
    setManualInput('');
    setSuggestions([]);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmManualAttendance = async () => {
    if (selectedStudent) {
      await processAttendance(selectedStudent.id);
      setIsConfirmModalOpen(false);
      setSelectedStudent(null);
    }
  };

  if (hasAccess === null) return <div className="scan-container"><p>⏳ Đang kiểm tra...</p></div>;
  if (!hasAccess) return <div className="scan-container"><h2>Bạn không có quyền Điểm danh đi lễ. <br />Vui lòng liên hệ Ban Điều Hành.</h2></div>;

  return (
    <div className="scan-container">
      {/* Badge Thông tin GLV Rút gọn */}
      <div className="glv-compact-badge">
        <span className="badge-role">⛪ ĐIỂM DANH ĐI LỄ</span>
        <span className="badge-name">GLV: {catechistHolyName} <strong>{catechistFullName}</strong></span>
      </div>

      {/* Tìm kiếm & Điểm danh thủ công Toàn trường */}
      <div className="manual-input-container">
        <input
          type="text"
          value={manualInput}
          onChange={(e) => setManualInput(e.target.value)}
          placeholder="🔍 Tìm Mã ID hoặc Tên để điểm danh..."
          className="manual-input"
        />
        {suggestions.length > 0 && (
          <ul className="suggestions-list">
            {suggestions.map(student => (
              <li
                key={student.id}
                onClick={() => handleSuggestionClick(student)}
                className="suggestion-item"
              >
                <strong>{student.id}</strong> - {student.holyName || ''} {student.fullName} {student.className ? `(${student.className})` : ''}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Bảng Nhật Ký Quét */}
      <div className="scan-results-table">
        <table>
          <thead>
            <tr>
              <th>Thời gian</th>
              <th>Mã ID</th>
              <th>Họ và Tên</th>
              <th>Trạng thái</th>
            </tr>
          </thead>
          <tbody>
            {scanResults.length > 0 ? (
              scanResults.map((result, index) => (
                <tr key={index} className={result.status}>
                  <td>{format(result.timestamp, 'HH:mm:ss')}</td>
                  <td className="td-id">{result.studentId}</td>
                  <td className="td-name">{result.name}</td>
                  <td className="td-status">
                    {result.status === 'processing' && <div className="spinner"></div>}
                    {result.status === 'success' && <span className="icon-success">✓</span>}
                    {result.status === 'error' && <span className="icon-error">✗</span>}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="empty-scan-msg">
                  Chưa có nhật ký điểm danh trong phiên này.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* NÚT FAB NỔI BÊN PHẢI */}
      <button className="fab-scan-btn mass-theme" onClick={() => setScanning(true)}>
        <img src={qrScanIcon} alt="Scan QR" className="fab-qr-icon" />
        <span className="fab-text">Quét mã QR</span>
      </button>

      {/* MODAL POPUP QUÉT MÃ */}
      {scanning && (
        <div className="qr-modal-overlay">
          <div className="qr-modal-card">
            <div className="qr-modal-header">
              <div className="header-title-box">
                <img src={qrScanIcon} alt="Scan" className="modal-header-icon" />
                <h4>Bỏ Mã QR Vào Khung Quét</h4>
              </div>
              <button className="qr-close-btn" onClick={() => setScanning(false)}>✕</button>
            </div>
            <div className="qr-modal-body">
              <div id="reader"></div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL XÁC NHẬN THỦ CÔNG */}
      {isConfirmModalOpen && selectedStudent && (
        <div className="qr-modal-overlay">
          <div className="confirm-card">
            <h3>Xác Nhận Điểm Danh Đi Lễ</h3>
            <div className="student-info-box">
              <p><strong>Mã ID:</strong> <span className="highlight">{selectedStudent.id}</span></p>
              <p><strong>Lớp:</strong> <span className="highlight">{selectedStudent.className || classMap?.[selectedStudent.classId] || 'Chưa phân lớp'}</span></p>
              <p><strong>Tên Thánh:</strong> {selectedStudent.holyName || 'Chưa có'}</p>
              <p><strong>Họ và Tên:</strong> {selectedStudent.fullName}</p>
            </div>
            <div className="confirm-actions">
              <button className="btn-confirm" onClick={handleConfirmManualAttendance}>Đồng ý</button>
              <button className="btn-cancel" onClick={() => setIsConfirmModalOpen(false)}>Hủy</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRScanner;